import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { unwrapConnection } from "@utils/utils";
import { FC, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, Headline, Small } from "@fronterahealth/frontera-ui-components";

import {
  ReportTemplateSuggestionCriteriaType,
  useCreateReportTemplateSuggestionCriteriaMutation,
  useGetReportTemplateSuggestionCriteriaQuery,
  useUpdateReportTemplateSuggestionCriteriaMutation,
} from "@api/graphql/types-and-hooks";
import { AddCriteriaForm } from "@pages/QASettings/AddCriteriaForm";
import { CriteriaCard } from "@pages/QASettings/CriteriaCard";

export const ReportTemplateQASettings: FC = () => {
  const { reportTemplateId } = useParams();
  const [showAddForm, setShowAddForm] = useState(false);

  const { data, refetch, isLoading, isFetching } = useGetReportTemplateSuggestionCriteriaQuery(
    { id: reportTemplateId ?? "" },
    { enabled: !!reportTemplateId },
  );

  const { mutateAsync: createCriteria, isPending: isCreating } = useCreateReportTemplateSuggestionCriteriaMutation({});

  const { mutateAsync: updateCriteria } = useUpdateReportTemplateSuggestionCriteriaMutation({});

  const criteriaList = (data?.getReportTemplate?.suggestionCriteria ?? []) as ReportTemplateSuggestionCriteriaType[];

  const handleCreate = async (name: string, category: string, content: string) => {
    await createCriteria({ reportTemplateId: reportTemplateId ?? "", name, content, category });
    setShowAddForm(false);
    refetch();
  };

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    await updateCriteria({ id, enabled });
    refetch();
  };

  const handleContentSave = async (id: string, content: string) => {
    await updateCriteria({ id, content });
    refetch();
  };

  const handleCategorySave = async (id: string, category: string) => {
    await updateCriteria({ id, category });
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-gray-300 p-12">
        <Small colorType="secondary">Loading...</Small>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col content-between items-center lg:flex-col lg:items-start">
      <div className="mt-4 flex w-full items-center justify-between">
        <Headline size="medium">{data?.getReportTemplate?.name ?? "Report Template"} QA Criteria</Headline>
        <Button
          appearance="primary"
          onClick={() => setShowAddForm((prev) => !prev)}
          text={showAddForm ? "Cancel" : "Add Criteria"}
        />
      </div>

      {showAddForm && (
        <AddCriteriaForm isSubmitting={isCreating} onSubmit={handleCreate} onCancel={() => setShowAddForm(false)} />
      )}

      {isFetching && !isLoading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          <Small colorType="secondary">Refreshing...</Small>
        </div>
      )}

      <div className="mt-6 flex w-full flex-col gap-4">
        {criteriaList.length > 0 ? (
          criteriaList.map((templateCriteria) => {
            if (!templateCriteria) return null;

            const otherTemplateNames: string[] = [];
            const connections = unwrapConnection(templateCriteria.suggestionCriteria.reportTemplates);
            if (connections && connections.length > 1) {
              otherTemplateNames.push(...connections.map(({ reportTemplate }) => reportTemplate.name));
            }

            return (
              <CriteriaCard
                key={templateCriteria.id}
                criteria={templateCriteria}
                relatedNames={otherTemplateNames}
                onToggleEnabled={handleToggleEnabled}
                onContentSave={handleContentSave}
                onCategorySave={handleCategorySave}
              />
            );
          })
        ) : (
          <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-gray-300 p-12">
            <Small colorType="secondary">No criteria yet. Use "Add Criteria" to create one.</Small>
          </div>
        )}
      </div>
    </div>
  );
};
